/**
 * @fileoverview Chip: a small button that is on or off, in a row of choices.
 *
 * The share screen's chip row is the pattern: a bordered box a thumb high
 * (2rem to see, 40px to hit: the hit area reaches 4px past the edge above and
 * below), quiet words when off, the theme's primary tint and a semibold word
 * when on. It says which it is with aria-pressed, or, for a choice of one
 * among several (the handle to print, the layout), with role="radio" and
 * aria-checked. A chip that cannot be chosen says why: its disabledReason is
 * its title (shown on pointing) and is read out after its words, so a
 * greyed chip is never a mystery.
 *
 * Where: the share screen (SharePlantDialog), the season review's choices
 * (SeasonReviewPage), the Plants filter sheet (PlantFilterSheet) and the
 * gardens directory's sort (GardensPage).
 * API: none.
 * Words: none; the caller gives the words and the disabled reason.
 *
 * @module Chip
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

/** What a chip is given: whether it is on, what pressing it does, and why it cannot be pressed. */
export interface ChipProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'role' | 'aria-pressed' | 'aria-checked'> {
  /** Whether the chip is on (pressed, or the one chosen). */
  pressed: boolean;
  /** One choice among several: role="radio" with aria-checked, instead of a toggle's aria-pressed. */
  radio?: boolean;
  /** An icon before the words, sized and coloured by the chip. */
  icon?: React.ReactNode;
  /** Why the chip is disabled, said on pointing at it and read out after its words; only used while it is disabled. */
  disabledReason?: string;
}

/**
 * A chip: on or off, a thumb high, with a reason when it is greyed.
 *
 * @param props - ChipProps; the rest pass through to the button.
 */
export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ pressed, radio = false, icon, disabledReason, disabled, title, className, children, ...rest }, ref) => {
    const reason = disabled ? disabledReason : undefined;
    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        role={radio ? 'radio' : undefined}
        aria-pressed={radio ? undefined : pressed}
        aria-checked={radio ? pressed : undefined}
        disabled={disabled}
        title={reason ?? title}
        className={cn(
          // 2rem to see; the hit area reaches 4px past the edge above and below, which makes 40px.
          "relative inline-flex min-h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-sm transition-colors before:absolute before:inset-x-0 before:-inset-y-1 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          pressed ? 'border-primary bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:text-foreground',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        {icon && <span className="inline-flex shrink-0 items-center" aria-hidden="true">{icon}</span>}
        {children}
        {reason && <span className="sr-only">{`, ${reason}`}</span>}
      </button>
    );
  },
);
Chip.displayName = 'Chip';
